import React from 'react'
import { MIN_ITEMS_VISIBLE } from './helpers'

/**
 * Persists the view length (number of items shown) to sessionStorage so that
 * navigating back to the page restores the same number of items.
 */
export const usePersistedViewLength = (
    storageKey: string, itemsPerPage: number
): [number, React.Dispatch<React.SetStateAction<number>>] => {
    const [currentViewLength, setCurrentViewLength] = React.useState<number>(() => {
        try {
            const saved = sessionStorage.getItem(`${storageKey}_v`)
            const parsed = saved !== null ? parseInt(saved, 10) : NaN
            if (!isNaN(parsed) && parsed > 0) return parsed
        } catch { /**/ }
        return Math.max(itemsPerPage, MIN_ITEMS_VISIBLE)
    })

    React.useEffect(() => {
        try {
            sessionStorage.setItem(`${storageKey}_v`, String(currentViewLength))
        } catch { /**/ }
    }, [currentViewLength, storageKey])

    return [currentViewLength, setCurrentViewLength]
}

/**
 * Saves the scroll position to sessionStorage on unmount, and restores it once
 * enough items are rendered (as signalled by readyToRestoreScroll becoming true).
 */
export const useScrollRestoration = (storageKey: string, readyToRestoreScroll: boolean): void => {
    // Ref so the unmount cleanup always captures the latest storageKey
    const storageKeyRef = React.useRef(storageKey)
    storageKeyRef.current = storageKey

    const scrollRestoredRef = React.useRef(false)
    const targetScrollYRef = React.useRef<number | null>(null)

    React.useLayoutEffect(() => {
        try {
            const saved = sessionStorage.getItem(`${storageKeyRef.current}_y`)
            if (saved !== null) {
                const parsed = parseInt(saved, 10)
                if (!isNaN(parsed) && parsed > 0) targetScrollYRef.current = parsed
            }
        } catch { /**/ }
        return () => {
            try {
                sessionStorage.setItem(`${storageKeyRef.current}_y`, String(window.scrollY))
                window.scrollTo(0, 0)
            } catch { /**/ }
        }
    }, [])

    React.useLayoutEffect(() => {
        if (!scrollRestoredRef.current && readyToRestoreScroll) {
            window.scrollTo(0, targetScrollYRef.current ?? 0)
            scrollRestoredRef.current = true
        }
    }, [readyToRestoreScroll])
}
